import React, {useMemo, useState} from "react";

import Card from "react-bootstrap/Card";
import {Container} from "react-bootstrap";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import {
  fahrzeugFilterOptionen,
  maengelFilterVorbelegung,
  maengelFiltern,
  maengelSeite,
  maengelSuchen,
  maengelZeilen,
  prioritaetAnzeige,
  statusFilterOptionen,
} from "../utils/maengel";

/**
 * Die vier Prioritätsstufen für die Auswahlliste, Beschriftung aus derselben
 * Quelle wie die Tabelle. Anders als die Fahrzeuge stehen sie fest: die Skala
 * ist vollständig bekannt, und eine Stufe, an der gerade kein Mangel hängt,
 * soll trotzdem wählbar bleiben — sonst wandert die Auswahl unter der Hand.
 */
const prioritaetFilterOptionen = ["1", "2", "3", "4"].map((stufe) => ({
  wert: stufe,
  text: prioritaetAnzeige(stufe, "offen").text,
}));

/**
 * Die Mängelliste über alle Fahrzeuge. Bewusst dünn: die Daten kommen als
 * Live-Listener aus der Wurzelkomponente, alle Ableitungen aus dem Datenmodell
 * sowie Filtern und Suchen stecken in `utils/maengel`. Der Suchbegriff kommt
 * aus der Suchleiste im Kopfbereich und damit ebenfalls von außen. Hier bleiben
 * der Filterzustand und das Rendern.
 */
const Maengel = ({
  maengel,
  fahrzeuge,
  isLoading,
  error,
  searchString,
  gotoFahrzeugPage,
}) => {
  const [filter, setFilter] = useState(maengelFilterVorbelegung);
  const [seite, setSeite] = useState(1);

  const zeilen = useMemo(() => {
    return maengelZeilen(maengel, fahrzeuge);
  }, [maengel, fahrzeuge]);

  // Die Fahrzeugauswahl hängt am ungefilterten Bestand: sie darf nicht
  // zusammenschrumpfen, sobald ein Filter greift.
  const fahrzeugOptionen = useMemo(() => {
    return fahrzeugFilterOptionen(zeilen);
  }, [zeilen]);

  // Die Suche aus dem Kopfbereich greift auf das Ergebnis der Filterzeile:
  // beides grenzt gemeinsam ein, statt sich gegenseitig zu überschreiben.
  const eingegrenzteZeilen = useMemo(() => {
    return maengelSuchen(maengelFiltern(zeilen, filter), searchString);
  }, [zeilen, filter, searchString]);

  // Der Suchbegriff kommt von außen: dass er sich geändert hat, ist erst beim
  // Rendern zu sehen. Die Seite hier gleich mitzuziehen, statt in einem Effekt
  // hinterherzuräumen, erspart den Zwischenzustand „alte Seite, neue Treffer".
  const [gemerkteSuche, setGemerkteSuche] = useState(searchString);

  if (gemerkteSuche !== searchString) {
    setGemerkteSuche(searchString);
    setSeite(1);
  }

  const blaetterSeite = useMemo(() => {
    return maengelSeite(eingegrenzteZeilen, seite);
  }, [eingegrenzteZeilen, seite]);

  const sichtbareZeilen = blaetterSeite.zeilen;

  // Jede neue Eingrenzung ist eine neue Liste: wer auf Seite 3 stand, stünde
  // sonst je nach Trefferzahl vor einer leeren Seite.
  const setzeFilter = (schluessel, wert) => {
    setFilter((vorher) => ({...vorher, [schluessel]: wert}));
    setSeite(1);
  };

  const filterZuruecksetzen = () => {
    setFilter(maengelFilterVorbelegung());
    setSeite(1);
  };

  const gesamtAnzahl = blaetterSeite.gesamt;
  // „Nichts gefunden" und „es gibt nichts" sind zwei verschiedene Nachrichten:
  // im ersten Fall hilft ein anderer Filter oder Suchbegriff, im zweiten nicht.
  const nurDurchEingrenzungLeer = gesamtAnzahl === 0 && zeilen.length > 0;
  // Ohne Suchbegriff war es allein die Filterzeile — die Meldung soll auf das
  // zeigen, woran der Nutzer wirklich gedreht hat.
  const leerMeldung =
    String(searchString ?? "").trim() === ""
      ? "Keine Mängel für die gewählten Filter"
      : "Keine Mängel für Suche und Filter";

  return (
    <Container>
      <Card className="statusColumn">
        <Card.Header style={{backgroundColor: "lightgray"}}>
          <div className="settingHeader">
            <div className="settingTitle">Mängel</div>
          </div>
        </Card.Header>
        <Card.Body>
          <Form style={{marginBottom: "12px"}}>
            <Row
              className="g-2 align-items-center"
              style={{marginBottom: "8px"}}
            >
              <Col md={3}>
                <Form.Select
                  aria-label="Status"
                  value={filter.status}
                  onChange={(event) => {
                    setzeFilter("status", event.target.value);
                  }}
                >
                  {statusFilterOptionen.map((option) => {
                    return (
                      <option key={option.wert} value={option.wert}>
                        Status: {option.text}
                      </option>
                    );
                  })}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select
                  aria-label="Fahrzeug"
                  value={filter.fahrzeugId}
                  onChange={(event) => {
                    setzeFilter("fahrzeugId", event.target.value);
                  }}
                >
                  <option value="">Fahrzeug: alle</option>
                  {fahrzeugOptionen.map((option) => {
                    return (
                      <option key={option.wert} value={option.wert}>
                        {option.text}
                      </option>
                    );
                  })}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select
                  aria-label="Priorität"
                  value={filter.prioritaet}
                  onChange={(event) => {
                    setzeFilter("prioritaet", event.target.value);
                  }}
                >
                  <option value="">Priorität: alle</option>
                  {prioritaetFilterOptionen.map((option) => {
                    return (
                      <option key={option.wert} value={option.wert}>
                        {option.text}
                      </option>
                    );
                  })}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Button
                  variant="secondary"
                  className="btn-header"
                  onClick={filterZuruecksetzen}
                >
                  Filter zurücksetzen
                </Button>
              </Col>
            </Row>
          </Form>

          {error !== "" ? <div className="loginError">{error}</div> : undefined}

          <Table striped bordered hover className="protokollTable">
            <thead>
              <tr>
                <th className="col-2">Eingang</th>
                <th className="col-1">Fahrzeug</th>
                <th className="col-2">Kategorie</th>
                <th className="col-4">Beschreibung</th>
                <th className="col-2">Priorität</th>
                <th className="col-1">Status</th>
              </tr>
            </thead>
            <tbody>
              {sichtbareZeilen.map((zeile) => {
                return (
                  <tr key={zeile.id}>
                    <td>{zeile.eingangText}</td>
                    <td>
                      <Button
                        variant="link"
                        className="p-0"
                        onClick={() => {
                          gotoFahrzeugPage(zeile.kennzeichen);
                        }}
                      >
                        {zeile.kennzeichen}
                      </Button>
                    </td>
                    <td>{zeile.kategorie}</td>
                    <td>{zeile.beschreibung}</td>
                    <td className={zeile.prioritaetKlasse}>
                      {zeile.prioritaetText}
                    </td>
                    <td>{zeile.statusText}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer className="statusFooter">
          <div>
            {isLoading ? "Lade Daten ..." : undefined}
            {!isLoading && error !== "" ? "Keine Daten geladen" : undefined}
            {!isLoading && error === "" && nurDurchEingrenzungLeer
              ? leerMeldung
              : undefined}
            {!isLoading && error === "" && gesamtAnzahl === 0 && !nurDurchEingrenzungLeer
              ? "Keine Einträge"
              : undefined}
            {!isLoading && error === "" && sichtbareZeilen.length === 1
              ? "1 Mangel auf dieser Seite"
              : undefined}
            {!isLoading && error === "" && sichtbareZeilen.length > 1
              ? sichtbareZeilen.length + " Mängel auf dieser Seite"
              : undefined}
          </div>
          <div style={{marginTop: "8px"}}>
            <Button
              variant="secondary"
              className="btn-header"
              onClick={() => {
                setSeite(blaetterSeite.seite - 1);
              }}
              disabled={blaetterSeite.seite <= 1}
            >
              Vorherige Seite
            </Button>
            <Button
              variant="secondary"
              className="btn-header"
              onClick={() => {
                setSeite(blaetterSeite.seite + 1);
              }}
              disabled={blaetterSeite.seite >= blaetterSeite.seitenAnzahl}
            >
              Nächste Seite
            </Button>
            <span style={{marginLeft: "8px"}}>
              Seite {blaetterSeite.seite} von {blaetterSeite.seitenAnzahl} (
              {blaetterSeite.gesamt} Einträge gesamt)
            </span>
          </div>
        </Card.Footer>
      </Card>
    </Container>
  );
};

export default Maengel;
